/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { PcgPipelineMetrics } from "../pcg-report/core"

/**
 * The analysis pipeline's whole-recording timing metrics of one guide
 * payload, in the shape the review export carries as `pipelineMetrics`.
 *
 * These are the UNCORRECTED values the pipeline measured over the whole
 * recording — not the viewer's selected-cycle rate, which the exam timing
 * consensus may have corrected for a half/double count. The exam-level Heart
 * Report roll-up works from these so that a suspected miscount is left out
 * and flagged instead of entering the summary at a corrected value.
 *
 * Returns null when the payload has no metrics block (a guide that did not
 * analyze).
 */
const finiteOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null

const buildPipelineMetrics = (payload: unknown): PcgPipelineMetrics | null => {
  const guide = (payload ?? {}) as {
    metrics?: { [key: string]: unknown } | null
    score?: unknown
    cardiacPlausibilityGate?: { status?: unknown; score?: unknown } | null
  }
  const metrics = guide.metrics
  if (!metrics || typeof metrics !== "object") {
    return null
  }
  const gate = guide.cardiacPlausibilityGate ?? {}
  return {
    hrBpm: finiteOrNull(metrics.hr_bpm),
    rrCv: finiteOrNull(metrics.rr_cv),
    sdRatio: finiteOrNull(metrics.sd_ratio),
    sysMs: finiteOrNull(metrics.sys_ms),
    diaMs: finiteOrNull(metrics.dia_ms),
    nS1: finiteOrNull(metrics.n_s1) ?? 0,
    s1s2AmpRatio: finiteOrNull(metrics.s1s2_amp_ratio),
    guideQuality: String(metrics.quality || "unknown").toUpperCase(),
    highConfidenceMarkerShare: finiteOrNull(metrics.pct_high_conf),
    // The S1/S2 layout (orientation) score is the payload's top-level score
    orientationScore: finiteOrNull(guide.score) ?? 0,
    gateStatus: String(gate.status || "unknown").toLowerCase(),
    gateScore: finiteOrNull(gate.score),
  }
}

export { buildPipelineMetrics }
